import { Shadow, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { ResponsesTitlesResponse } from '@genial/services/dist/models';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import React, { useState } from 'react';

import { useRecoilQuery } from '../../../../../../../../hooks/useRecoilQuery';
import { CustomerDetailStackParamList } from '../../../../../../../../routes/types';
import { directTreasuryStatementsDetailsQuery } from '../../../../../../../../store/customerDetail/stataments/selectors';
import { LateralBar } from '../../../../../../../NewApplication/Product/components/LateralBar';
import { validateAndFormatValue, messageError } from '../../Table/constants';

import { DetailsList } from './components/DetailsList';
import { Header } from './components/Header';
import { MoreDetailsDesktop } from './components/MoreDetails/desktop';
import { MoreDetailsMobile } from './components/MoreDetails/mobile';
import { parseDirectTreasuryDetailsData, parseMainData } from './constants';

interface DirectTreasuryItemProps {
  data: ResponsesTitlesResponse;
}
export interface DirectTreasuryDetailsItemProps {
  iofValue: string;
  irValue: string;
  cblcCustodyFee: string;
  custodyFeeBroker: string;
  profitabilityMonth: string;
  profitabilityYear: string;
  profitabilityTwelveMonths: string;
  netValue: string;
  titleId: string;
  purchaseDate: string;
  actualBalance: string | number;
  purchasePrice: string;
  investedValue: string;
  agreedRate: string;
  grossValue: string;
  accumulatedProfitability: string;
  calendarDays: string | number;
  irTaxRate: string;
}

export const DirectTreasuryItem: React.FC<DirectTreasuryItemProps> = ({ data }) => {
  const { params } = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const accountNumber = params?.accountNumber;
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const [isOpened, setIsOpened] = useState(false);

  const directTreasuryStatements = useRecoilQuery(
    directTreasuryStatementsDetailsQuery({
      accountNumber,
      callEndpoint: isOpened,
      titleId: data.titleId,
    })
  );

  const main = parseMainData(data);
  const directTreasuryDetailsData = parseDirectTreasuryDetailsData(
    directTreasuryStatements?.data?.data || []
  );

  const title = data?.titleName || messageError;
  const currentValue = validateAndFormatValue(data?.actualValue, 'money');

  return (
    <Shadow
      marginBottom={Spacing['inset.nano']}
      level={3}
      borderRadius={BoxRadius.sm}
      my={Spacing['inline.nano']}>
      <VStack borderRadius={BoxRadius.sm}>
        <LateralBar
          backgroundColor={Color['product.tesouro']}
          borderTopLeftRadius={BoxRadius.sm}
          borderBottomLeftRadius={BoxRadius.sm}
        />
        <VStack p={Spacing['inline.xxxs']} space={Spacing['inline.xxxs']}>
          <Header
            handleOpenInfo={() => setIsOpened((state) => !state)}
            isOpened={isOpened}
            currentValue={currentValue}
            title={title}
          />
          {isMobile && isOpened && (
            <MoreDetailsMobile
              refresher={directTreasuryStatements.refresher}
              isLoading={!!directTreasuryStatements.loading}
              isError={!!directTreasuryStatements.error}
              data={main}
              details={directTreasuryDetailsData}
            />
          )}
          {!isMobile && <DetailsList data={main} />}
          {!isMobile && isOpened && (
            <MoreDetailsDesktop
              refresher={directTreasuryStatements.refresher}
              isError={!!directTreasuryStatements.error}
              isLoading={!!directTreasuryStatements.loading}
              details={directTreasuryDetailsData}
            />
          )}
        </VStack>
      </VStack>
    </Shadow>
  );
};
